import { c as _c } from "react/compiler-runtime";
import { Notification } from '@/components/Atoms/Notification/Notification';
import { toastStore } from '@/services/Toast/toastStore';
import type { HTMLAttributes } from 'react';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import styles from './ToastNotificationContainer.module.css';

// `popover` isn't in React's typings yet, so we cast it in alongside the other attributes
const popoverProp = {
  popover: 'manual'
} as HTMLAttributes<HTMLDivElement>;

/**
 * Renders the global toast stack.
 *
 * Uses the Popover API so the container is promoted to the browser top layer —
 * it floats above the entire page (nav, dialogs, transformed/clipped ancestors)
 * without a portal or any z-index. Client-only: toasts are only triggered by
 * front-end actions, so this is a no-op during SSR.
 */
export const ToastNotificationContainer = () => {
  const $ = _c(11);
  const toasts = useSyncExternalStore(toastStore.subscribe, toastStore.getSnapshot, toastStore.getServerSnapshot);
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setMounted(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== toasts.length) {
    t2 = () => {
      const el = ref.current;
      if (!el || typeof el.showPopover !== "function") {
        return;
      }
      try {
        if (toasts.length > 0) {
          el.showPopover();
        } else {
          el.hidePopover();
        }
      } catch {}
    };
    $[2] = toasts.length;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== mounted || $[5] !== toasts.length) {
    t3 = [toasts.length, mounted];
    $[4] = mounted;
    $[5] = toasts.length;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(t2, t3);
  if (!mounted) {
    return null;
  }
  let t4;
  if ($[7] !== toasts) {
    t4 = toasts.map(_temp);
    $[7] = toasts;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <div {...popoverProp} ref={ref} className={styles.host} role="status" aria-live="polite">{t4}</div>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
function _temp(toast) {
  return <div key={toast.id} data-cds-state={toast.state} className={styles.toast}><Notification state={toast.state} className={styles.notification} onClose={() => toastStore.dismiss(toast.id)}>{toast.message}</Notification></div>;
}